import moment from 'moment';

import { closeWebsocket } from 'src/config/Apollo';

import { config } from '../config/Config';
import { AuthenticationError } from '../errorHandling/AuthenticationError';
import {
  AuthData,
  Credentials,
  PasswordGrant,
  RefreshGrant,
  SingleSignOnRequestData,
  SingleSignOnRequestResponse,
} from '../models/Auth';
import http, { Http } from '../services/Http';

export const delay = (ms: number = 0) => new Promise((resolve) => setTimeout(resolve, ms));

export class Auth {
  private static authRealm: string = config.defaultAuthRealm;
  private static AUTH_KEY: string = 'auth';

  // Semi-mutex to prevent multiple requests from `Http` from requesting a
  // bunch of tokens at the same time. (Not a real mutex because Javascript)
  private static currentlyFetching: boolean = false;

  public constructor(private instanceHttp: Http) {}

  public tokenValid(tokenType: string): boolean {
    const authData = this.authData();
    if (!authData || !authData[tokenType]) {
      return false;
    }

    let payload;
    try {
      payload = JSON.parse(atob(authData[tokenType].split('.')[1]));
    } catch {
      return false;
    }

    if (!payload.exp) {
      return false;
    }

    // Give a small buffer in case the request is right at the expiry time
    const expiryDateTime = moment(payload.exp * 1000).subtract(60, 'seconds');

    return expiryDateTime > moment();
  }

  /**
   * Save the authData object to Local Storage and set the feature flags
   * in rematch.
   * @param authData pure AuthData object to save
   */
  private saveAuthData(authData: AuthData): AuthData {
    localStorage.setItem(Auth.AUTH_KEY, JSON.stringify(authData));

    return authData;
  }

  public clearAuthData(): void {
    localStorage.removeItem(Auth.AUTH_KEY);
  }

  public async login(credentials: Credentials): Promise<AuthData> {
    const data: PasswordGrant = {
      grant_type: 'password',
      scope: 'webapp',
      username: credentials.email,
      password: credentials.password,
    };

    try {
      const authData = await this.instanceHttp.makeUnAuthenticatedRequest<PasswordGrant, AuthData>(
        'POST',
        this.tokenUrl(),
        data,
        true,
      );

      return this.saveAuthData(authData);
    } catch (err: any) {
      switch (err.code) {
        // Unauthenticated, i.e. an Authorization header is not provided.
        case 401: {
          throw new AuthenticationError(config.messages.incorrectCredentials);
        }
        // Forbidden, i.e. the user doesn't have access to login.
        case 403: {
          throw new AuthenticationError(config.messages.incorrectCredentials);
        }
        // Too many requests, i.e. they've incorrectly logged in too many times.
        case 429:
          throw new AuthenticationError(err.body.error);
        default:
          throw new AuthenticationError(config.messages.connectionProblemError);
      }
    }
  }

  public async ssoLoginRequest(data: SingleSignOnRequestData): Promise<string> {
    try {
      const { ssoUrl } =
        await this.instanceHttp.makeUnAuthenticatedRequest<SingleSignOnRequestResponse>(
          'GET',
          `${Auth.authRealm}/api/sso_settings/?email=${data.email}&provider=true`,
        );
      return ssoUrl ? ssoUrl : Promise.reject('notifications.singleSignOnNotEligible');
    } catch (error: any) {
      const errorMsg =
        error.code >= 500
          ? config.messages.connectionProblemError
          : 'notifications.singleSignOnNotEligible';

      return Promise.reject(errorMsg);
    }
  }

  public async ssoLogin(authString: string): Promise<AuthData> {
    localStorage.setItem(Auth.AUTH_KEY, authString);
    const authData = this.authData();

    if (!authData) {
      this.clearAuthData();
      return Promise.reject('Auth data is not valid');
    }

    if (!this.tokenValid('access_token')) {
      this.clearAuthData();
      return Promise.reject('Access token not valid');
    }

    return this.saveAuthData(authData);
  }

  public async logout(reason: string): Promise<string> {
    const authData = this.authData();

    this.clearAuthData();

    if (!authData || !authData.refresh_token) {
      return Promise.resolve('Success');
    }

    const data = { refresh_token: authData.refresh_token };
    closeWebsocket();

    this.instanceHttp.makeUnAuthenticatedRequest<object, Response>(
      'DELETE',
      this.tokenUrl(),
      data,
      true,
    );

    return reason;
  }

  // Refresh the SSO Token from Single Sign on. SSO will only provide a
  // refresh token hence the placeholder values for access_token, expires_in, and token_type
  public async refreshSSOToken(refreshToken): Promise<AuthData> {
    // Pull the token expiration from the issued at (iat) and the expiration (exp) claim
    const tokenPayload = JSON.parse(atob(refreshToken.split('.')[1]));
    const expiresIn = tokenPayload.exp - tokenPayload.iat;

    // Auth Data legacy interface does not pass linting - hence the need to disable linting
    const authData = {
      access_token: '',
      refresh_token: refreshToken,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      expires_in: expiresIn,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      token_type: 'Bearer',
    };
    this.saveAuthData(authData);

    return this.refresh();
  }

  // Refresh the access token if needed
  public async refresh(): Promise<AuthData> {
    while (Auth.currentlyFetching) {
      await delay(100);
    }

    Auth.currentlyFetching = true;

    try {
      const authData = this.authData();

      if (!authData || !authData.refresh_token) {
        return Promise.reject('Auth data missing');
      }

      if (this.tokenValid('access_token')) {
        return Promise.resolve(authData);
      }

      if (!this.tokenValid('refresh_token')) {
        return Promise.reject('Refresh token is invalid');
      }

      const data: RefreshGrant = {
        grant_type: 'refresh_token',
        scope: 'webapp',
        refresh_token: authData.refresh_token,
      };

      try {
        const newAuthData = await this.instanceHttp.makeUnAuthenticatedRequest<
          RefreshGrant,
          AuthData
        >('POST', this.tokenUrl(), data, true);

        authData.access_token = newAuthData.access_token;
        if (newAuthData.refresh_token) {
          authData.refresh_token = newAuthData.refresh_token;
        }
        return this.saveAuthData(authData);
      } catch (err) {
        throw new AuthenticationError(config.messages.sessionExpired);
      }
    } finally {
      Auth.currentlyFetching = false;
    }
  }

  public async isAuthenticated(): Promise<AuthData | boolean> {
    try {
      const authData = await this.refresh();
      return authData;
    } catch {
      return false;
    }
  }

  public authData(): AuthData | null {
    const authString = localStorage.getItem(Auth.AUTH_KEY);
    if (!authString) {
      return null;
    }

    return JSON.parse(authString) as AuthData;
  }

  private tokenUrl(): string {
    return `${Auth.authRealm}/${config.tokenPath}`;
  }
}

const auth = new Auth(http);

export default auth;
